// components/AddBudgetModal.jsx
import { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_PATH from '../apiConfig';
import { showToast } from './Toast';
import { formatCurrency } from '../utils';

const AddBudgetModal = ({ availableAmount, currentMonth, preselectedCategoryId, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    category_id: preselectedCategoryId || '',
    notes: ''
  });

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API_BASE_PATH}/budget-categories`);
        setCategories(response.data || []);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const assignedAmount = parseFloat(formData.amount);

    // Safety check
    if (assignedAmount > availableAmount) {
      showToast(`Cannot allocate ${formatCurrency(assignedAmount)}. Only ${formatCurrency(availableAmount)} available.`, 'error');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        type: 'budget',
        assigned_amount: assignedAmount, // For zero-based budgeting
        amount: 0, // No budget limit, just tracking assigned amount
        category_id: formData.category_id || null,
        date: `${currentMonth}-01`,
        notes: formData.notes
      };

      await axios.post(`${API_BASE_PATH}/budget-items`, payload);
      showToast('Budget created and money assigned', 'success');
      onSave();
    } catch (error) {
      console.error('Error adding budget:', error);
      showToast('Failed to add budget', 'error');
    } finally {
      setLoading(false);
    }
  };

  const quickAmounts = [50, 100, 200, 500];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            <i className="fas fa-piggy-bank"></i>
            Create Budget
          </h2>
          <button className="modal-close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div style={{
              padding: '12px 16px',
              background: 'var(--success-light)',
              border: '2px solid var(--success-color)',
              borderRadius: 'var(--border-radius)',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                READY TO ASSIGN
              </div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--success-color)' }}>
                {formatCurrency(availableAmount || 0)}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                Assign money to this budget from your available income
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Budget Name *</label>
              <input
                type="text"
                name="name"
                className="form-control"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Groceries, Gas, Dining Out, Entertainment"
                autoFocus
                required
              />
              <small style={{ color: 'var(--text-secondary)', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                Create a budget for each spending category you want to track
              </small>
            </div>

            <div className="form-group">
              <label className="form-label">Category</label>
              <select
                name="category_id"
                className="form-control"
                value={formData.category_id}
                onChange={handleChange}
              >
                <option value="">Uncategorized</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.icon && <i className={`fas ${cat.icon}`}></i>} {cat.name}
                  </option>
                ))}
              </select>
              <small style={{ color: 'var(--text-secondary)', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                Choose a category to group this budget (optional)
              </small>
            </div>

            <div className="form-group">
              <label className="form-label">Amount to Assign * (Max: {formatCurrency(availableAmount || 0)})</label>
              <input
                type="number"
                name="amount"
                className="form-control"
                value={formData.amount}
                onChange={handleChange}
                step="0.01"
                max={availableAmount || 0}
                placeholder="0.00"
                required
              />
              <small style={{ color: 'var(--text-secondary)', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                How much money do you want to assign to this budget?
              </small>

              <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                {quickAmounts.map(amt => (
                  <button
                    key={amt}
                    type="button"
                    className="btn btn-secondary"
                    style={{ padding: '4px 10px', fontSize: '12px', flex: 1 }}
                    onClick={() => setFormData(prev => ({ ...prev, amount: Math.min(amt, availableAmount || 0).toString() }))}
                    disabled={amt > (availableAmount || 0)}
                  >
                    ${amt}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea
                name="notes"
                className="form-control"
                value={formData.notes}
                onChange={handleChange}
                rows="2"
                placeholder="Optional notes..."
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Creating...
                </>
              ) : (
                <>
                  <i className="fas fa-check"></i>
                  Create Budget
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddBudgetModal;